import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, User } from "lucide-react";
import { guestMixRefusedBy, MEN_ALONE_SPA_SLUGS } from "@majourneys/shared";
import { BUTTON, KIT, PRIMARY, chatT, kitPanel } from "./kit-tokens";

type Guest = "woman" | "man";

/** "mandara-thai-massage-marrakech" -> "Mandara Thai Massage". */
export function nameFromSlug(slug: string): string {
  return slug
    .replace(/-(marrakech|casablanca|agadir|fes|fez|rabat|tangier|essaouira)$/i, "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** The spa that does take a man alone, to offer when this one refuses. */
export function menAloneAlternative(currentSlug: string): string | null {
  for (const s of MEN_ALONE_SPA_SLUGS) if (s !== currentSlug) return s;
  return null;
}

/**
 * C23: who is coming to the spa, as people rather than letters: a pink
 * woman, a green man, added with "+ Woman" / "+ Man" and removed by tapping
 * the avatar.
 *
 * The rule is the shared one (`guestMixRefusedBy`): every spa except the
 * men-alone allow-list needs at least one woman in the group. When this spa
 * refuses, it says so with the booking flow's own wording and offers the
 * spa that does take men on their own, by sending that request to the chat.
 */
export function SpaGuestMix({
  slug,
  venueName,
  lang,
  onSend,
  locked = false,
  alternativeName,
}: {
  slug: string;
  venueName: string;
  lang: string;
  onSend: (text: string) => void;
  locked?: boolean;
  /** Display name of the men-alone spa; derived from its slug when absent. */
  alternativeName?: string;
}) {
  const { t: baseT, i18n } = useTranslation();
  const t = chatT(i18n, baseT, lang);
  const [people, setPeople] = useState<Guest[]>([]);
  const [sent, setSent] = useState(false);

  const women = people.filter((p) => p === "woman").length;
  const men = people.length - women;
  const refused = guestMixRefusedBy(slug, { women, men });
  const altSlug = refused ? menAloneAlternative(slug) : null;
  const altName = alternativeName || (altSlug ? nameFromSlug(altSlug) : "");
  const done = locked || sent;

  const mix = () => {
    const parts: string[] = [];
    if (women > 0) parts.push(t("ai_concierge.kit.spa.women", { count: women }));
    if (men > 0) parts.push(t("ai_concierge.kit.spa.men", { count: men }));
    return parts.length === 2 ? t("ai_concierge.kit.spa.and", { a: parts[0], b: parts[1] }) : (parts[0] ?? "");
  };

  return (
    <div data-testid="kit-spa-mix" className="flex flex-col gap-2.5 p-3" style={kitPanel()}>
      <p className="text-[11px] font-bold uppercase tracking-[0.1em]" style={{ color: KIT.muted }}>
        {t("guest_mix.title")}
      </p>

      <div className="flex min-h-[40px] items-center">
        {people.map((p, i) => (
          <button
            key={i}
            type="button"
            disabled={done}
            aria-label={t(p === "woman" ? "ai_concierge.kit.spa.remove_woman" : "ai_concierge.kit.spa.remove_man")}
            onClick={() => setPeople((x) => x.filter((_, j) => j !== i))}
            className="-ms-2 grid h-10 w-10 place-items-center rounded-full border-2 border-white transition-opacity duration-200 first:ms-0"
            style={{
              background: p === "woman" ? KIT.pinkTint : KIT.greenTint,
              color: p === "woman" ? KIT.pink : KIT.green,
            }}
          >
            <User size={16} aria-hidden />
          </button>
        ))}
        <span className="ms-3 text-[12.5px] tabular-nums" style={{ color: KIT.muted }} aria-live="polite">
          {people.length > 0 ? mix() : t("ai_concierge.kit.spa.empty")}
        </span>
      </div>

      {!done ? (
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => setPeople((x) => [...x, "woman"])}
            className="min-h-[36px] rounded-full text-[13px] font-semibold"
            style={{ ...BUTTON, color: KIT.navy }}
          >
            + {t("guest_mix.woman")}
          </button>
          <button
            type="button"
            onClick={() => setPeople((x) => [...x, "man"])}
            className="min-h-[36px] rounded-full text-[13px] font-semibold"
            style={{ ...BUTTON, color: KIT.navy }}
          >
            + {t("guest_mix.man")}
          </button>
        </div>
      ) : null}

      {refused && !done ? (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-[12px] px-3 py-2.5 text-[12.5px] leading-[1.5]"
          style={{ background: KIT.goldTint, color: KIT.goldDark }}
        >
          <span>{t("guest_mix.men_accompanied", { venue: venueName })}</span>
          {altSlug ? (
            <button
              type="button"
              onClick={() => {
                setSent(true);
                onSend(t("ai_concierge.kit.spa.alternative_send", { venue: altName, mix: mix() }));
              }}
              className="min-h-[36px] self-start rounded-full px-3.5 text-[12.5px] font-semibold"
              style={{ ...BUTTON, color: KIT.navy }}
            >
              {t("ai_concierge.kit.spa.alternative", { venue: altName })}
            </button>
          ) : null}
        </div>
      ) : null}

      {done ? (
        <p className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: KIT.navy }}>
          <Check size={13} color={KIT.gold} strokeWidth={3} aria-hidden /> {mix()}
        </p>
      ) : (
        <button
          type="button"
          disabled={people.length === 0 || refused}
          onClick={() => {
            setSent(true);
            onSend(t("ai_concierge.kit.spa.send", { venue: venueName, mix: mix() }));
          }}
          className="min-h-[44px] w-full rounded-full text-[13.5px] font-semibold disabled:opacity-50"
          style={PRIMARY}
        >
          {t("ai_concierge.kit.spa.confirm")}
        </button>
      )}
    </div>
  );
}
